/*
 * PixelLab Resource Loader
 * Loads resources while providing progress updates.
 */
;(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define([], function () {
      return (root.PxLoader = factory())
    })
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory()
  } else {
    // Browser globals
    root.PxLoader = factory()
  }
})(this, function () {
  function PxLoader(settings) {
    // merge settings with defaults
    settings = settings || {}
    this.settings = settings

    // how frequently we poll resources for progress
    if (settings.statusInterval == null) {
      settings.statusInterval = 5000 // every 5 seconds by default
    }

    // delay before logging since last progress change
    if (settings.loggingDelay == null) {
      settings.loggingDelay = 20 * 1000 // log stragglers after 20 secs
    }

    // stop waiting if no progress has been made in the moving time window
    if (settings.noProgressTimeout == null) {
      settings.noProgressTimeout = Infinity // do not stop waiting by default
    }

    var entries = [],
      // holds resources to be loaded with their status
      completionListeners = [],
      progressListeners = [],
      timeStarted,
      progressChanged = Date.now()

    /**
     * The status of a resource
     * @enum {number}
     */
    var ResourceState = {
      QUEUED: 0,
      WAITING: 1,
      LOADED: 2,
      ERROR: 3,
      TIMEOUT: 4,
    }

    // places non-array values into an array.
    var ensureArray = function (val) {
      if (val == null) {
        return []
      }

      if (Array.isArray(val)) {
        return val
      }

      return [val]
    }

    // add an entry to the list of resources to be loaded
    this.add = function (resource) {
      // TODO: would be better to create a base class for all resources and
      // initialize the PxLoaderTags there rather than overwritting tags here
      resource.tags = new PxLoaderTags(resource.tags)

      // ensure priority is set
      if (resource.priority == null) {
        resource.priority = Infinity
      }

      entries.push({
        resource: resource,
        status: ResourceState.QUEUED,
      })
    }

    this.addProgressListener = function (callback, tags) {
      progressListeners.push({
        callback: callback,
        tags: new PxLoaderTags(tags),
      })
    }

    this.addCompletionListener = function (callback, tags) {
      completionListeners.push({
        tags: new PxLoaderTags(tags),
        callback: function (e) {
          if (e.completedCount === e.totalCount) {
            callback(e)
          }
        },
      })
    }

    // creates a comparison function for resources
    var getResourceSort = function (orderedTags) {
      // helper to get the top tag's order for a resource
      orderedTags = ensureArray(orderedTags)
      var getTagOrder = function (entry) {
        var resource = entry.resource,
          bestIndex = Infinity
        for (var i = 0; i < resource.tags.length; i++) {
          for (var j = 0; j < Math.min(orderedTags.length, bestIndex); j++) {
            if (resource.tags.all[i] === orderedTags[j] && j < bestIndex) {
              bestIndex = j
              if (bestIndex === 0) {
                break
              }
            }
            if (bestIndex === 0) {
              break
            }
          }
        }
        return bestIndex
      }
      return function (a, b) {
        // check tag order first
        var aOrder = getTagOrder(a),
          bOrder = getTagOrder(b)
        if (aOrder < bOrder) {
          return -1
        }
        if (aOrder > bOrder) {
          return 1
        }

        // now check priority
        if (a.priority < b.priority) {
          return -1
        }
        if (a.priority > b.priority) {
          return 1
        }
        return 0
      }
    }

    this.start = function (orderedTags) {
      timeStarted = Date.now()

      // first order the resources
      var compareResources = getResourceSort(orderedTags)
      entries.sort(compareResources)

      // trigger requests for each resource
      for (var i = 0, len = entries.length; i < len; i++) {
        var entry = entries[i]
        entry.status = ResourceState.WAITING
        entry.resource.start(this)
      }

      // do an initial status check soon since items may be loaded from the cache
      setTimeout(statusCheck, 100)
    }

    var statusCheck = function () {
      var checkAgain = false,
        noProgressTime = Date.now() - progressChanged,
        timedOut = noProgressTime >= settings.noProgressTimeout,
        shouldLog = noProgressTime >= settings.loggingDelay

      for (var i = 0, len = entries.length; i < len; i++) {
        var entry = entries[i]
        if (entry.status !== ResourceState.WAITING) {
          continue
        }

        // see if the resource has loaded
        if (entry.resource.checkStatus) {
          entry.resource.checkStatus()
        }

        // if still waiting, mark as timed out or make sure we check again
        if (entry.status === ResourceState.WAITING) {
          if (timedOut) {
            entry.resource.onTimeout()
          } else {
            checkAgain = true
          }
        }
      }

      // log any resources that are still pending
      if (shouldLog && checkAgain) {
        log()
      }

      if (checkAgain) {
        setTimeout(statusCheck, settings.statusInterval)
      }
    }

    this.isBusy = function () {
      for (var i = 0, len = entries.length; i < len; i++) {
        if (
          entries[i].status === ResourceState.QUEUED ||
          entries[i].status === ResourceState.WAITING
        ) {
          return true
        }
      }
      return false
    }

    var onProgress = function (resource, statusType) {
      var entry = null,
        i,
        len,
        listeners,
        listener,
        shouldCall

      // find the entry for the resource
      for (i = 0, len = entries.length; i < len; i++) {
        if (entries[i].resource === resource) {
          entry = entries[i]
          break
        }
      }

      // we have already updated the status of the resource
      if (entry == null || entry.status !== ResourceState.WAITING) {
        return
      }
      entry.status = statusType
      progressChanged = Date.now()

      // ensure completion listeners fire after progress listeners
      listeners = progressListeners.concat(completionListeners)

      // fire callbacks for interested listeners
      for (i = 0, len = listeners.length; i < len; i++) {
        listener = listeners[i]
        if (listener.tags.length === 0) {
          // no tags specified so always tell the listener
          shouldCall = true
        } else {
          // listener only wants to hear about certain tags
          shouldCall = resource.tags.intersects(listener.tags)
        }

        if (shouldCall) {
          sendProgress(entry, listener)
        }
      }
    }

    this.onLoad = function (resource) {
      onProgress(resource, ResourceState.LOADED)
    }
    this.onError = function (resource) {
      onProgress(resource, ResourceState.ERROR)
    }
    this.onTimeout = function (resource) {
      onProgress(resource, ResourceState.TIMEOUT)
    }

    // sends a progress report to a listener
    var sendProgress = function (updatedEntry, listener) {
      // find stats for all the resources the caller is interested in
      var completed = 0,
        total = 0,
        i,
        len,
        entry,
        includeResource
      for (i = 0, len = entries.length; i < len; i++) {
        entry = entries[i]
        includeResource = false

        if (listener.tags.length === 0) {
          // no tags specified so always tell the listener
          includeResource = true
        } else {
          includeResource = entry.resource.tags.intersects(listener.tags)
        }

        if (includeResource) {
          total++
          if (
            entry.status === ResourceState.LOADED ||
            entry.status === ResourceState.ERROR ||
            entry.status === ResourceState.TIMEOUT
          ) {
            completed++
          }
        }
      }

      listener.callback({
        // info about the resource that changed
        resource: updatedEntry.resource,

        // should we expose StatusType instead?
        loaded: updatedEntry.status === ResourceState.LOADED,
        error: updatedEntry.status === ResourceState.ERROR,
        timeout: updatedEntry.status === ResourceState.TIMEOUT,

        // updated stats for all resources
        completedCount: completed,
        totalCount: total,
      })
    }

    // prints the status of each resource to the console
    var log = (this.log = function (showAll) {
      if (!window.console) {
        return
      }

      var elapsedSeconds = Math.round((Date.now() - timeStarted) / 1000)
      window.console.log('PxLoader elapsed: ' + elapsedSeconds + ' sec')

      for (var i = 0, len = entries.length; i < len; i++) {
        var entry = entries[i]
        if (!showAll && entry.status !== ResourceState.WAITING) {
          continue
        }

        var message = 'PxLoader: #' + i + ' ' + entry.resource.getName()
        switch (entry.status) {
          case ResourceState.QUEUED:
            message += ' (Not Started)'
            break
          case ResourceState.WAITING:
            message += ' (Waiting)'
            break
          case ResourceState.LOADED:
            message += ' (Loaded)'
            break
          case ResourceState.ERROR:
            message += ' (Error)'
            break
          case ResourceState.TIMEOUT:
            message += ' (Timeout)'
            break
        }

        if (entry.resource.tags.length > 0) {
          message += ' Tags: [' + entry.resource.tags.all.join(',') + ']'
        }

        window.console.log(message)
      }
    })
  }

  // Tag object to handle tag intersection; once created not meant to be changed
  // Performance rationale: http://jsperf.com/lists-indexof-vs-in-operator/3

  function PxLoaderTags(values) {
    this.all = []
    this.first = null // cache the first value
    this.length = 0

    // holds values as keys for quick lookup
    this.lookup = {}

    if (values) {
      // first fill the array of all values
      if (Array.isArray(values)) {
        // copy the array of values, just to be safe
        this.all = values.slice(0)
      } else if (typeof values === 'object') {
        for (var key in values) {
          if (values.hasOwnProperty(key)) {
            this.all.push(key)
          }
        }
      } else {
        this.all.push(values)
      }

      // cache the length and the first value
      this.length = this.all.length
      if (this.length > 0) {
        this.first = this.all[0]
      }

      // set values as object keys for quick lookup during intersection test
      for (var i = 0; i < this.length; i++) {
        this.lookup[this.all[i]] = true
      }
    }
  }

  // compare this object with another; return true if they share at least one value
  PxLoaderTags.prototype.intersects = function (other) {
    // handle empty values case
    if (this.length === 0 || other.length === 0) {
      return false
    }

    // only a single value to compare?
    if (this.length === 1 && other.length === 1) {
      return this.first === other.first
    }

    // better to loop through the smaller object
    if (other.length < this.length) {
      return other.intersects(this)
    }

    // loop through every key to see if there are any matches
    for (var key in this.lookup) {
      if (other.lookup[key]) {
        return true
      }
    }

    return false
  }

  return PxLoader
})

// PxLoader plugin to load images
;(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define(['pxloader'], function (PxLoader) {
      return (root.PxLoaderImage = factory(PxLoader))
    })
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory(require('pxloader'))
  } else {
    // Browser globals
    root.PxLoaderImage = factory(root.PxLoader)
  }
})(this, function (PxLoader) {
  function PxLoaderImage(url, tags, priority, options) {
    options = options || {}

    var self = this,
      loader = null,
      img

    img = this.img = new Image()
    if (options.origin) {
      img.crossOrigin = options.origin
    }

    this.tags = tags
    this.priority = priority

    var onReadyStateChange = function () {
      if (self.img.readyState !== 'complete') {
        return
      }

      onLoad()
    }

    var onLoad = function () {
      loader.onLoad(self)
      cleanup()
    }

    var onError = function () {
      loader.onError(self)
      cleanup()
    }

    var onTimeout = function () {
      loader.onTimeout(self)
      cleanup()
    }

    var cleanup = function () {
      self.unbind('load', onLoad)
      self.unbind('readystatechange', onReadyStateChange)
      self.unbind('error', onError)
    }

    this.start = function (pxLoader) {
      // we need the loader ref so we can notify upon completion
      loader = pxLoader

      // NOTE: Must add event listeners before the src is set. We
      // also need to use the readystatechange because sometimes
      // load doesn't fire when an image is in the cache.
      self.bind('load', onLoad)
      self.bind('readystatechange', onReadyStateChange)
      self.bind('error', onError)

      self.img.src = url
    }

    // called by PxLoader to check status of image (fallback in case
    // the event listeners are not triggered).
    this.checkStatus = function () {
      onReadyStateChange()
    }

    // called by PxLoader when it is no longer waiting
    this.onTimeout = function () {
      if (self.img.complete) {
        onLoad()
      } else {
        onTimeout()
      }
    }

    // returns a name for the resource that can be used in logging
    this.getName = function () {
      return url
    }

    // cross-browser event binding
    this.bind = function (eventName, eventHandler) {
      self.img.addEventListener(eventName, eventHandler, false)
    }

    // cross-browser event un-binding
    this.unbind = function (eventName, eventHandler) {
      self.img.removeEventListener(eventName, eventHandler, false)
    }
  }

  // add a convenience method to PxLoader for adding an image
  PxLoader.prototype.addImage = function (url, tags, priority, options) {
    var imageLoader = new PxLoaderImage(url, tags, priority, options)
    this.add(imageLoader)

    // return the img element to the caller
    return imageLoader.img
  }

  return PxLoaderImage
})

;('use strict')

function _instanceof2(left, right) {
  if (
    right != null &&
    typeof Symbol !== 'undefined' &&
    right[Symbol.hasInstance]
  ) {
    return !!right[Symbol.hasInstance](left)
  } else {
    return left instanceof right
  }
}
function _instanceof(left, right) {
  if (
    right != null &&
    typeof Symbol !== 'undefined' &&
    right[Symbol.hasInstance]
  ) {
    return !!right[Symbol.hasInstance](left)
  } else {
    return _instanceof2(left, right)
  }
}
function _typeof(o) {
  '@babel/helpers - typeof'

  return (
    (_typeof =
      'function' == typeof Symbol && 'symbol' == typeof Symbol.iterator
        ? function (o) {
            return typeof o
          }
        : function (o) {
            return o &&
              'function' == typeof Symbol &&
              o.constructor === Symbol &&
              o !== Symbol.prototype
              ? 'symbol'
              : typeof o
          }),
    _typeof(o)
  )
}
function _classCallCheck(instance, Constructor) {
  if (!_instanceof(instance, Constructor)) {
    throw new TypeError('Cannot call a class as a function')
  }
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i]
    descriptor.enumerable = descriptor.enumerable || false
    descriptor.configurable = true
    if ('value' in descriptor) descriptor.writable = true
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor)
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps)
  if (staticProps) _defineProperties(Constructor, staticProps)
  Object.defineProperty(Constructor, 'prototype', {
    writable: false,
  })
  return Constructor
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key)
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true,
    })
  } else {
    obj[key] = value
  }
  return obj
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, 'string')
  return _typeof(key) === 'symbol' ? key : String(key)
}
function _toPrimitive(input, hint) {
  if (_typeof(input) !== 'object' || input === null) return input
  var prim = input[Symbol.toPrimitive]
  if (prim !== undefined) {
    var res = prim.call(input, hint || 'default')
    if (_typeof(res) !== 'object') return res
    throw new TypeError('@@toPrimitive must return a primitive value.')
  }
  return (hint === 'string' ? String : Number)(input)
}
var ua = navigator.userAgent.toLowerCase()
var isMobile = /ios|iphone|ipod|ipad|android/.test(ua)
var Slider = /*#__PURE__*/ (function () {
  function Slider(option) {
    var _this = this
    _classCallCheck(this, Slider)
    // mousedown event of slider
    _defineProperty(this, 'onMouseDown', function (ev) {
      _this.flag = true
      ev.stopPropagation()
      ev.preventDefault()
    })
    // mousemove event of document
    _defineProperty(this, 'onMouseMove', function (event) {
      if (_this.flag) {
        var ev = isMobile ? event.touches[0] : event
        // get mouse offsetY for box
        var offsetY = ev.clientY - _this.boxTop
        // get top offset
        var top = 0
        if (offsetY > _this.boxHeight) {
          top = _this.boxHeight
        } else if (offsetY >= 0) {
          top = offsetY
        } else {
          top = 0
        }
        var result = _this.getCurrentValue(top)
        if (_this.onChange) {
          // send to onChange function when it exist
          if (result.current !== _this.value) {
            // emit change event when value is different
            _this.value = result.current
            _this.onChange(result.current)
          }
        }

        // set top position of slider
        _this.thumbEl.style.transform = 'translateY('.concat(
          result.top - _this.topOff,
          'px)',
        )
      }
    })
    // mouseup event of document
    _defineProperty(this, 'onMouseUp', function () {
      _this.flag = false
    })
    if (!option || !option.thumbEl) {
      throw new Error('Slider needed thumbEl')
    }
    this.thumbEl = option.thumbEl
    this.onChange = option.onChange
    this.stepEnable = !option.stepEnable ? false : true
    this.flag = false
    this.min = option.min || 0
    this.max = option.max || 100
    this.step = option.step || 1
    this.topOff = option.topOff || 0
    this.value = option.value || 0
    this.init()
  }
  _createClass(Slider, [
    {
      key: 'init',
      value: function init() {
        this.render()
        this.addListener()
      },
    },
    {
      key: 'render',
      value: function render() {
        // get sliderBox
        this.sliderBox = this.thumbEl.parentElement
        this.boxHeight = this.sliderBox.offsetHeight
        this.boxTop = this.sliderBox.getBoundingClientRect().top
        this.setTopByValue()
      },

      // set slider default position with default value when init render
    },
    {
      key: 'setTopByValue',
      value: function setTopByValue() {
        var top = ((this.value / this.max) * this.boxHeight).toFixed(2)
        this.thumbEl.style.transform = 'translateY('.concat(
          top - this.topOff,
          'px)',
        )
      },

      // get current value and slider top position
    },
    {
      key: 'getCurrentValue',
      value: function getCurrentValue(top) {
        var current = (top / this.boxHeight) * this.max
        current = Math.round(current / this.step)
        current = current * this.step
        return {
          current: current,
          top: this.stepEnable
            ? (current / this.max) * this.boxHeight.toFixed(2)
            : top,
        }
      },

      // add listener when page or component loaded
    },
    {
      key: 'addListener',
      value: function addListener() {
        this.thumbEl.addEventListener(
          isMobile ? 'touchstart' : 'mousedown',
          this.onMouseDown,
        )
        document.addEventListener(
          isMobile ? 'touchmove' : 'mousemove',
          this.onMouseMove,
        )
        document.addEventListener(
          isMobile ? 'touchend' : 'mouseup',
          this.onMouseUp,
        )
      },

      // remove listener when page or component destroy
    },
    {
      key: 'removeListener',
      value: function removeListener() {
        this.thumbEl.removeEventListener(
          isMobile ? 'touchstart' : 'mousedown',
          this.onMouseDown,
        )
        document.removeEventListener(
          isMobile ? 'touchmove' : 'mousemove',
          this.onMouseMove,
        )
        document.removeEventListener(
          isMobile ? 'touchend' : 'mouseup',
          this.onMouseUp,
        )
      },
    },
    {
      key: 'destroy',
      value: function destroy() {
        this.removeListener()
      },
    },
  ])
  return Slider
})()

function _instanceof(left, right) {
  if (
    right != null &&
    typeof Symbol !== 'undefined' &&
    right[Symbol.hasInstance]
  ) {
    return !!right[Symbol.hasInstance](left)
  } else {
    return left instanceof right
  }
}
function _typeof(o) {
  '@babel/helpers - typeof'
  return (
    (_typeof =
      'function' == typeof Symbol && 'symbol' == typeof Symbol.iterator
        ? function (o) {
            return typeof o
          }
        : function (o) {
            return o &&
              'function' == typeof Symbol &&
              o.constructor === Symbol &&
              o !== Symbol.prototype
              ? 'symbol'
              : typeof o
          }),
    _typeof(o)
  )
}
function _classCallCheck(instance, Constructor) {
  if (!_instanceof(instance, Constructor)) {
    throw new TypeError('Cannot call a class as a function')
  }
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i]
    descriptor.enumerable = descriptor.enumerable || false
    descriptor.configurable = true
    if ('value' in descriptor) descriptor.writable = true
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor)
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps)
  if (staticProps) _defineProperties(Constructor, staticProps)
  Object.defineProperty(Constructor, 'prototype', { writable: false })
  return Constructor
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key)
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true,
    })
  } else {
    obj[key] = value
  }
  return obj
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, 'string')
  return _typeof(key) === 'symbol' ? key : String(key)
}
function _toPrimitive(input, hint) {
  if (_typeof(input) !== 'object' || input === null) return input
  var prim = input[Symbol.toPrimitive]
  if (prim !== undefined) {
    var res = prim.call(input, hint || 'default')
    if (_typeof(res) !== 'object') return res
    throw new TypeError('@@toPrimitive must return a primitive value.')
  }
  return (hint === 'string' ? String : Number)(input)
}
var SliderX = /*#__PURE__*/ (function () {
  function SliderX(option) {
    var _this = this
    _classCallCheck(this, SliderX)
    // mousedown event of slider
    _defineProperty(this, 'onMouseDown', function () {
      _this.flag = true
    })
    // mousemove event of document
    _defineProperty(this, 'onMouseMove', function (event) {
      if (_this.flag) {
        // get mouse offsetX for box
        var offsetX = event.touches[0].clientX - _this.boxLeft

        // get left offset
        var left = 0
        if (offsetX > _this.boxWidth) {
          left = _this.boxWidth
        } else if (offsetX >= 0) {
          left = offsetX
        } else {
          left = 0
        }
        var result = _this.getCurrentValue(left)
        if (_this.onChange) {
          // send to onChange function when it exist
          if (result.current !== _this.value) {
            // emit change event when value is different
            _this.value = result.current
            _this.onChange(result.current)
          }
        }

        // set left position of slider
        _this.thumbEl.style.transform = 'translateX('.concat(
          result.left - _this.leftOff,
          'px)',
        )
      }
    })
    // mouseup event of document
    _defineProperty(this, 'onMouseUp', function () {
      _this.flag = false
    })
    if (!option || !option.thumbEl) {
      throw new Error('Slider needed thumbEl')
    }
    this.thumbEl = option.thumbEl
    this.onChange = option.onChange
    this.stepEnable = !option.stepEnable ? false : true
    this.flag = false
    this.min = option.min || 0
    this.max = option.max || 100
    this.step = option.step || 1
    this.leftOff = option.leftOff || 0
    this.value = option.value || 0
    this.init()
  }
  _createClass(SliderX, [
    {
      key: 'init',
      value: function init() {
        this.render()
        this.addListener()
      },
    },
    {
      key: 'render',
      value: function render() {
        // get sliderBox
        this.sliderBox = this.thumbEl.parentElement
        this.boxWidth = this.sliderBox.offsetWidth
        this.boxLeft = this.sliderBox.getBoundingClientRect().left
        this.setTopByValue()
      },

      // set slider default position with default value when init render
    },
    {
      key: 'setTopByValue',
      value: function setTopByValue() {
        var left = ((this.value / this.max) * this.boxWidth).toFixed(2)
        this.thumbEl.style.transform = 'translateX('.concat(
          left - this.leftOff,
          'px)',
        )
      },

      // get current value and slider left position
    },
    {
      key: 'getCurrentValue',
      value: function getCurrentValue(left) {
        var current = (left / this.boxWidth) * this.max
        current = Math.round(current / this.step)
        current = current * this.step
        return {
          current: current,
          left: this.stepEnable
            ? (current / this.max) * this.boxWidth.toFixed(2)
            : left,
        }
      },

      // add listener when page or component loaded
    },
    {
      key: 'addListener',
      value: function addListener() {
        this.thumbEl.addEventListener('touchstart', this.onMouseDown)
        document.addEventListener('touchmove', this.onMouseMove)
        document.addEventListener('touchend', this.onMouseUp)
      },

      // remove listener when page or component destroy
    },
    {
      key: 'removeListener',
      value: function removeListener() {
        this.thumbEl.removeEventListener('touchstart', this.onMouseDown)
        document.removeEventListener('touchmove', this.onMouseMove)
        document.removeEventListener('touchend', this.onMouseUp)
      },
    },
    {
      key: 'destroy',
      value: function destroy() {
        this.removeListener()
      },
    },
  ])
  return SliderX
})()

;('use strict')

function _slicedToArray(arr, i) {
  return (
    _arrayWithHoles(arr) ||
    _iterableToArrayLimit(arr, i) ||
    _unsupportedIterableToArray(arr, i) ||
    _nonIterableRest()
  )
}
function _nonIterableRest() {
  throw new TypeError(
    'Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.',
  )
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return
  if (typeof o === 'string') return _arrayLikeToArray(o, minLen)
  var n = Object.prototype.toString.call(o).slice(8, -1)
  if (n === 'Object' && o.constructor) n = o.constructor.name
  if (n === 'Map' || n === 'Set') return Array.from(o)
  if (n === 'Arguments' || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n))
    return _arrayLikeToArray(o, minLen)
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]
  return arr2
}
function _iterableToArrayLimit(r, l) {
  var t =
    null == r
      ? null
      : ('undefined' != typeof Symbol && r[Symbol.iterator]) || r['@@iterator']
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1
    try {
      if (((i = (t = t.call(r)).next), 0 === l)) {
        if (Object(t) !== t) return
        f = !1
      } else
        for (
          ;
          !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l);
          f = !0
        );
    } catch (r) {
      ;(o = !0), (n = r)
    } finally {
      try {
        if (!f && null != t.return && ((u = t.return()), Object(u) !== u))
          return
      } finally {
        if (o) throw n
      }
    }
    return a
  }
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr
}
function debounce(func, wait, immediate) {
  var timeout
  return function () {
    var context = this
    var args = arguments
    if (timeout) clearTimeout(timeout) // timeout 不为null
    if (immediate) {
      var callNow = !timeout // 第一次会立即执行，以后只有事件执行后才会再次触发
      timeout = setTimeout(function () {
        timeout = null
      }, wait)
      if (callNow) {
        return func.apply(context, args)
      }
    } else {
      timeout = setTimeout(function () {
        return func.apply(context, args)
      }, wait)
    }
  }
}
function renderImg2Canvas(ctx, img, options) {
  if (img && img.complete) {
    var opt = options || {}
    var width = opt.width || img.width
    var height = opt.height || img.height
    ctx.canvas.width = width
    ctx.canvas.height = height
    var centerX = (ctx.canvas.width - width) / 2
    var centerY = (ctx.canvas.height - height) / 2
    ctx.clearRect(0, 0, width, height)
    try {
      ctx.drawImage(img, centerX, centerY, width, height)
    } catch (error) {}
  }
}
function autoSupplement(val) {
  return ''.concat(val).padStart(3, '0')
}
function loadImage(imgPrefixUrl) {
  var format =
    arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'png'
  var imgCount = arguments.length > 2 ? arguments[2] : undefined
  var onComplete = arguments.length > 3 ? arguments[3] : undefined
  var step = arguments.length > 4 ? arguments[4] : undefined
  var loader = new PxLoader()
  var ret = []
  for (var index = 0; index < imgCount; index++) {
    ret[index] = loader.addImage(
      imgPrefixUrl + (index + 1) * (step || 1) + '.' + format,
      imgPrefixUrl,
    )
  }
  loader.start()
  loader.addCompletionListener(function (e) {
    onComplete(ret, loader)
  })
}
function loopTop(element) {
  var offsetTop =
    arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0
  if (element === null) {
    return offsetTop
  }
  offsetTop = offsetTop + element.offsetTop
  return loopTop(element.offsetParent, offsetTop)
}
function getModulePosition(element) {
  var offsetTop = loopTop(element, 0)
  var scrollBegin =
    offsetTop /
    (document.documentElement.scrollHeight -
      document.documentElement.clientHeight)
  scrollBegin = Math.max(scrollBegin, 0)
  var scrollEnd =
    (offsetTop + element.clientHeight - document.documentElement.clientHeight) /
    (document.documentElement.scrollHeight -
      document.documentElement.clientHeight)
  var scrollDistance = scrollEnd - scrollBegin
  return {
    scrollBegin: scrollBegin,
    scrollDistance: scrollDistance,
  }
}
function computeFrameRatio(currentFrameRatio, currentFrameIndex) {
  var frameIndexBegin =
    arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0
  var frameIndexEnd = arguments.length > 3 ? arguments[3] : undefined
  if (currentFrameIndex <= frameIndexBegin) return 0
  if (currentFrameIndex >= frameIndexEnd) return 1
  return (
    (currentFrameRatio - frameIndexBegin) / (frameIndexEnd - frameIndexBegin)
  )
}
function getTimelineFragmentRatio(ratio, _ref) {
  var _ref2 = _slicedToArray(_ref, 2),
    begin = _ref2[0],
    end = _ref2[1]
  if (ratio <= begin) return 0
  if (ratio >= end) return 1
  var ret = (ratio - begin) / (end - begin)
  if (ret > 0.97) return 1
  return (ratio - begin) / (end - begin)
}
function getIntervalAnimationRatio(ratio, _ref3) {
  var _ref4 = _slicedToArray(_ref3, 4),
    begin_1 = _ref4[0],
    end_1 = _ref4[1],
    begin_2 = _ref4[2],
    end_2 = _ref4[3]
  var start = getTimelineFragmentRatio(ratio, [begin_1, end_1])
  var end = getTimelineFragmentRatio(ratio, [begin_2, end_2])
  if (end) return 1 - end
  if (start) return start
}
function isRatioRange(_ref5, ratio) {
  var _ref6 = _slicedToArray(_ref5, 2),
    begin = _ref6[0],
    end = _ref6[1]
  if (ratio > begin && ratio < end) return true
  return false
}
var triggerAnimation = function triggerAnimation(value, callback) {
  return function (currentValue) {}
}
function initVideoInnerAnimation(videoNode, timeArray, callback) {
  var playing = false
  var currentTimeIndex = -1
  videoNode.addEventListener('play', function (e) {
    playing = true
    currentTimeIndex = -1
  })
  videoNode.addEventListener('ended', function (e) {
    playing = false
    callback(-1)
  })
  videoNode.addEventListener('timeupdate', function (e) {
    var currentTime = Math.floor(videoNode.currentTime * 1000)
    var ret = -1
    for (var index = 0; index < timeArray.length; index++) {
      var time = timeArray[index]
      if (currentTime >= time) ret = index
    }
    if (ret > currentTimeIndex) {
      currentTimeIndex = ret
      callback(ret)
    }
  })
}

/**
 * 同步两段视频的进度
 * @param {*} video_01
 * @param {*} video_02
 */
function syncVideoCurrentTime(video_01, video_02) {
  video_02.play()
  video_01.play()
  setInterval(function () {
    var gap = Math.abs(video_01.currentTime - video_02.currentTime)
    if (gap <= 0.5) return
    video_02.currentTime = video_01.currentTime
  }, 1000)
}
function playRangeFrame(canvasNode, frameStart, frameEnd) {
  var ctx = canvasNode.getContext('2d')
  var index = frameStart
  var frameList = []
  var timer = setInterval(function () {
    if (!playing) return
    renderImg2Canvas(ctx, frameList[index])
    index += 1
    if (index > frameEnd) clearInterval(timer)
  }, 60)
  var playing = false
  return {
    setFrameList: function setFrameList(val) {
      frameList = val
    },
    play: function play() {
      if (!frameList.length) return
      playing = true
    },
    stop: function stop() {
      playing = false
    },
  }
}
function repeatPlayFrameRange(canvasNode, start, end) {
  var ctx = canvasNode.getContext('2d')
  var index = start
  var _play = false
  var frameList = []
  setInterval(function () {
    if (!_play) return
    index += 1
    renderImg2Canvas(ctx, frameList[index])
    if (index > end) index = start
  }, 300)
  return {
    setFrameList: function setFrameList(val) {
      frameList = val
    },
    play: function play() {
      if (!frameList.length) return
      _play = true
    },
    pause: function pause() {
      _play = false
    },
  }
}
function getScrollElementShowOffsetTop(node) {
  var offsetTop = loopTop(node)
  var height = node.clientHeight
  return offsetTop - document.documentElement.clientHeight + height * 0.8
}
